import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '@/api/client';
import SiteFooter from '@/components/SiteFooter';
import { CheckCircle, XCircle, Loader } from 'lucide-react';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const listingId = params.get('listing_id');

  const [status, setStatus] = useState('loading'); // loading | success | failed
  const [resolvedListingId, setResolvedListingId] = useState(listingId || '');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    let timer;

    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        const { status: st, listing_id } = res.data;
        if (listing_id) setResolvedListingId(listing_id);
        if (st === 'completed') {
          setStatus('success');
        } else if (st === 'failed' || st === 'expired') {
          setStatus('failed');
        } else {
          // Still pending — retry up to 10 times
          setAttempts(a => {
            if (a >= 10) { setStatus('failed'); return a; }
            timer = setTimeout(poll, 2000);
            return a + 1;
          });
        }
      } catch {
        if (!cancelled) setStatus('failed');
      }
    };

    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg)', color: 'var(--text)', fontFamily: 'var(--font-sans)' }}>
      <div className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-md w-full text-center space-y-6">

          {status === 'loading' && (
            <>
              <div className="flex justify-center">
                <Loader size={48} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
              </div>
              <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-serif)' }}>Confirming payment…</h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Please wait while we verify your payment. This usually takes a few seconds.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center">
                <CheckCircle size={52} strokeWidth={1.5} />
              </div>
              <h1 className="text-3xl font-light" style={{ fontFamily: 'var(--font-serif)' }}>Payment confirmed</h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Your listing fee has been received. Your listing is now live on The Listing Room.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                {resolvedListingId && (
                  <Link
                    to={`/listings/${resolvedListingId}`}
                    data-testid="view-listing-btn"
                    className="px-6 py-2.5 text-sm font-medium border transition-colors"
                    style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}
                  >
                    View listing
                  </Link>
                )}
                <Link
                  to="/dashboard/listings"
                  data-testid="go-to-listings-btn"
                  className="px-6 py-2.5 text-sm font-medium text-white transition-colors"
                  style={{ backgroundColor: 'var(--text)' }}
                >
                  My listings
                </Link>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="flex justify-center">
                <XCircle size={52} strokeWidth={1.5} style={{ color: 'var(--text-muted)' }} />
              </div>
              <h1 className="text-3xl font-light" style={{ fontFamily: 'var(--font-serif)' }}>Payment could not be verified</h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                We were unable to confirm your payment. If you were charged, please contact support. Otherwise, try publishing again.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link
                  to="/dashboard/listings"
                  data-testid="back-to-listings-btn"
                  className="px-6 py-2.5 text-sm font-medium text-white"
                  style={{ backgroundColor: 'var(--text)' }}
                >
                  Back to listings
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
